import { Product } from '../types/store';
import { getProductSlug } from './urlHelpers';
import { getProductMainImage } from '../data/products';

export const PRIMARY_DOMAIN = 'https://healthstore.store';

export interface SeoConfig {
  title?: string;
  description?: string;
  keywords?: string;
  canonicalPath?: string;
  isPrivate?: boolean;
  ogType?: 'website' | 'product';
  ogImage?: string;
  product?: Product | null;
}

export function getProductSeoData(product: Product) {
  const flavor = (product.flavor || '').toLowerCase();
  const titleLower = (product.title || '').toLowerCase();
  const slug = getProductSlug(product);

  let title = `${product.title} | Health Store Pakistan`;
  let description = `Buy ${product.title} online in Pakistan at Health Store. ${product.shortDescription || 'Original MalRich weight gain nutrition powder enriched with essential vitamins, minerals, and milk protein.'} Fast Cash on Delivery across Pakistan.`;
  let keywords = 'weight gain milk powder Pakistan, healthy weight gain powder, weight gain supplement Pakistan, MalRich Healthy Weight Gain Milk Powder, buy weight gain powder online Pakistan, Health Store Pakistan';

  if (titleLower.includes('buy 2 get 1') || slug.includes('buy-2-get-1')) {
    title = 'MalRich Buy 2 Get 1 Free Weight Gain Bundle | Health Store Pakistan';
    description = 'Get 3 full-sized tins of MalRich Healthy Weight Gain Milk Powder for the price of 2. Best value weight gain supplement pack with Free Cash on Delivery across Pakistan.';
    keywords = 'weight gain bundle Pakistan, buy 2 get 1 free MalRich, weight gain value pack Pakistan, weight gain milk powder deal Pakistan, MalRich supplement Pakistan';
  } else if (titleLower.includes('family') || slug.includes('family')) {
    title = 'MalRich Family Bundle Mega Nutrition Pack (4 Tins) | Health Store Pakistan';
    description = 'Buy MalRich Family Nutrition Mega Bundle (4 Tins) online in Pakistan. Complete household nutritional health pack for weight gain and vitality with Free Cash on Delivery.';
    keywords = 'family nutrition pack Pakistan, MalRich family bundle, health supplements Pakistan, weight gain powder family pack Pakistan, MalRich milk powder Pakistan';
  } else if (flavor.includes('mango') || titleLower.includes('mango')) {
    title = 'MalRich Mango Balanced Diet Supplement (500g) | Health Store Pakistan';
    description = 'Buy MalRich Mango Balanced Diet Supplement in Pakistan. Daily nutritional energy booster powder enriched with Magnesium, Zinc, Calcium, and vitamins with Cash on Delivery.';
    keywords = 'balanced diet supplement Pakistan, MalRich mango powder, nutrition supplement Pakistan, daily health supplement Pakistan, MalRich Pakistan, energy drink Pakistan';
  } else if (flavor.includes('choc') || titleLower.includes('chocolate')) {
    title = 'MalRich Chocolate Healthy Weight Gain Milk Powder (400g) | Health Store Pakistan';
    description = 'Buy MalRich Chocolate Healthy Weight Gain Milk Powder online in Pakistan. Rich Dutch cocoa weight gain supplement packed with high-grade proteins, minerals & Cash on Delivery.';
    keywords = 'chocolate weight gain powder Pakistan, MalRich chocolate milk powder, healthy weight gain supplement Pakistan, buy weight gain shake Pakistan, milk protein Pakistan';
  } else if (flavor.includes('straw') || titleLower.includes('strawberry')) {
    title = 'MalRich Strawberry Healthy Weight Gain Milk Powder (400g) | Health Store Pakistan';
    description = 'Buy MalRich Strawberry Healthy Weight Gain Milk Powder online in Pakistan. Nutritious strawberry milkshake supplement for healthy mass building & Cash on Delivery.';
    keywords = 'strawberry weight gain powder Pakistan, MalRich strawberry milk powder, weight gain drink Pakistan, nutrition supplements Pakistan, healthy growth powder Pakistan';
  } else if (flavor.includes('vanil') || titleLower.includes('vanilla')) {
    title = 'MalRich Healthy Weight Gain Milk Powder (Vanilla 400g) | Health Store Pakistan';
    description = 'Buy MalRich Healthy Weight Gain Milk Powder (Vanilla 400g) online in Pakistan. High protein weight gain supplement with vital vitamins, minerals & Cash on Delivery.';
    keywords = 'weight gain milk powder Pakistan, healthy weight gain powder, weight gain supplement Pakistan, MalRich Healthy Weight Gain Milk Powder, buy weight gain powder online Pakistan, MalRich vanilla Pakistan';
  }

  return { title, description, keywords, slug };
}

export function updatePageSeo(config: SeoConfig) {
  if (typeof document === 'undefined') return;

  try {
    let {
      title = 'Health Store | Weight Gain & Nutrition Products in Pakistan',
      description = 'Shop weight gain and nutrition products online in Pakistan. Buy MalRich weight gain products and health supplements with Cash on Delivery.',
      keywords = 'Health Store Pakistan, Weight Gain Products Pakistan, MalRich Weight Gain Powder Pakistan, Weight Gain Milk Powder Pakistan, Nutrition Supplements Pakistan, Buy Weight Gain Products Online Pakistan, Health Supplements Pakistan, Cash on Delivery Pakistan',
      canonicalPath = '/',
      isPrivate = false,
      ogType = 'website',
      ogImage = `${PRIMARY_DOMAIN}/assets/images/malrich_hero_main_banner_1786910615726.webp`,
      product = null,
    } = config;

    // If product is provided, auto-compute rich product SEO if not overridden
    if (product) {
      const productSeo = getProductSeoData(product);
      if (!config.title) title = productSeo.title;
      if (!config.description) description = productSeo.description;
      if (!config.keywords) keywords = productSeo.keywords;
      if (!config.canonicalPath) canonicalPath = `/products/${productSeo.slug}`;
      ogType = 'product';
    }

    // 1. Page Title
    document.title = title;

    // Helper to safely set meta tag content
    const setMeta = (nameOrProperty: string, content: string, isProperty = false) => {
      const selector = isProperty
        ? `meta[property="${nameOrProperty}"]`
        : `meta[name="${nameOrProperty}"]`;
      let el = document.querySelector(selector);
      if (!el) {
        el = document.createElement('meta');
        if (isProperty) {
          el.setAttribute('property', nameOrProperty);
        } else {
          el.setAttribute('name', nameOrProperty);
        }
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    // 2. Meta Description
    setMeta('description', description);

    // 3. Meta Keywords
    setMeta('keywords', keywords);

    // 4. Robots Meta Tag
    if (isPrivate) {
      setMeta('robots', 'noindex, nofollow');
    } else {
      setMeta(
        'robots',
        'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'
      );
    }

    // 5. Canonical Link
    const cleanPath = canonicalPath.startsWith('/') ? canonicalPath : `/${canonicalPath}`;
    const canonicalUrl = `${PRIMARY_DOMAIN}${cleanPath === '/' ? '' : cleanPath}`;
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', canonicalUrl);

    // 6. Open Graph Meta Tags
    setMeta('og:title', title, true);
    setMeta('og:description', description, true);
    setMeta('og:url', canonicalUrl, true);
    setMeta('og:type', ogType, true);
    setMeta('og:site_name', 'Health Store', true);
    setMeta('og:image', ogImage, true);

    // 7. Twitter / X Card Meta Tags
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', title);
    setMeta('twitter:description', description);
    setMeta('twitter:image', ogImage);

    // 8. Dynamic Product JSON-LD Structured Data
    const SCRIPT_ID = 'dynamic-product-jsonld';
    let scriptEl = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;

    if (product) {
      const productSlug = getProductSlug(product);
      const productUrl = `${PRIMARY_DOMAIN}/products/${productSlug}`;
      let resolvedImage = getProductMainImage(product);
      if (resolvedImage.startsWith('/')) {
        resolvedImage = `${PRIMARY_DOMAIN}${resolvedImage}`;
      }

      const productSchema = {
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: product.title,
        image: resolvedImage,
        description: product.description || product.shortDescription || `${product.title} - MalRich Health Store Pakistan`,
        sku: product.id || productSlug,
        brand: {
          '@type': 'Brand',
          name: product.brand || 'MalRich',
        },
        offers: {
          '@type': 'Offer',
          url: productUrl,
          priceCurrency: 'PKR',
          price: product.price ? product.price.toString() : '2200',
          priceValidUntil: '2027-12-31',
          itemCondition: 'https://schema.org/NewCondition',
          availability: product.inStock !== false ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
          seller: {
            '@type': 'Organization',
            name: 'Health Store',
            url: PRIMARY_DOMAIN,
          },
        },
      };

      if (!scriptEl) {
        scriptEl = document.createElement('script');
        scriptEl.id = SCRIPT_ID;
        scriptEl.type = 'application/ld+json';
        document.head.appendChild(scriptEl);
      }
      scriptEl.textContent = JSON.stringify(productSchema);
    } else {
      if (scriptEl && scriptEl.parentNode) {
        scriptEl.parentNode.removeChild(scriptEl);
      }
    }
  } catch {
    // Non-blocking catch for safety
  }
}
